import { useState, useEffect } from "react";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { updateGST } from "../../../services/GSTService";

const AddGST = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const gstData = location.state?.gst || null;

  const [gst, setGst] = useState({
    cgst: "",
    sgst: "",
    type: "Update Base Price",
  });

  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id && gstData) {
      setGst({
        cgst: gstData.cgst || "",
        sgst: gstData.sgst || "",
        type: gstData.type || "Update Base Price",
      });
    } else if (!id) {
      // Reset to default for new entries
      setGst({
        cgst: "",
        sgst: "",
        type: "Update Base Price",
      });
    }
  }, [id, gstData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear any existing validation error for this field as the user types
    setError((prev) => {
      const next = { ...prev };
      if (next[name]) {
        delete next[name];
      }
      // also clear any generic submit error when user edits
      if (next.submit) {
        delete next.submit;
      }
      return next;
    });

    setGst((prev) => ({
      ...prev,
      [name]: value === "" ? "" : parseFloat(value) || "",
    }));
  };

  const calculateTotal = () => {
    const cgst = parseFloat(gst.cgst) || 0;
    const sgst = parseFloat(gst.sgst) || 0;
    return (cgst + sgst).toFixed(2);
  };

  const validate = () => {
    const newErrors = {};

    if (gst.cgst === "") {
      newErrors.cgst = "CGST is required";
    } else if (isNaN(gst.cgst) || gst.cgst < 0 || gst.cgst > 100) {
      newErrors.cgst = "CGST must be a number between 0 and 100";
    }

    if (gst.sgst === "") {
      newErrors.sgst = "SGST is required";
    } else if (isNaN(gst.sgst) || gst.sgst < 0 || gst.sgst > 100) {
      newErrors.sgst = "SGST must be a number between 0 and 100";
    }

    if (!gst.type.trim()) {
      newErrors.type = "Type is required";
    }

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError({});

    if (!validate()) {
      setLoading(false);
      return;
    }

    const confirm = await swal({
      title: `Confirm ${gst.type}`,
      text: `This will apply GST changes for ${gst.type}. Do you want to continue?`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    });

    if (!confirm) {
      setLoading(false);
      return;
    }

    try {
      const updateType = gst.type === "Update Base Price" ? "Base Price" : "MRP";

      const payload = {
        cgst: parseFloat(gst.cgst),
        sgst: parseFloat(gst.sgst),
        total_gst: parseFloat(calculateTotal()),
        update_type: updateType,
        ...(id && { id }),
      };

      await updateGST(payload);

      await swal(
        id ? "Updated!" : "Added!",
        `GST ${id ? "updated" : "added"} successfully`,
        "success"
      );

      navigate("/gst");
    } catch (err) {
      console.error("Error while saving GST:", err);
      await swal("Error", "Failed to save GST. Please try again.", "error");
      setError({ submit: "Failed to save GST. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-body">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
              <Link to="/gst">
                <Button variant="primary">Back</Button>
              </Link>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-4 mb-3">
                  <label className="form-label">
                    CGST (%)
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control ${error.cgst ? "is-invalid" : ""}`}
                    name="cgst"
                    value={gst.cgst}
                    onChange={handleChange}
                    placeholder="Enter CGST %"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                  {error.cgst && <small className="text-danger d-block">{error.cgst}</small>}
                </div>

                <div className="col-xl-4 mb-3">
                  <label className="form-label">
                    SGST (%)
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control ${error.sgst ? "is-invalid" : ""}`}
                    name="sgst"
                    value={gst.sgst}
                    onChange={handleChange}
                    placeholder="Enter SGST %"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                  {error.sgst && <small className="text-danger d-block">{error.sgst}</small>}
                </div>

                <div className="col-xl-4 mb-3">
                  <label className="form-label">
                    TOTAL GST (%)
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="total_gst"
                    value={calculateTotal()}
                    disabled
                    placeholder="Auto calculated"
                  />
                  <small className="text-muted">Auto-calculated</small>
                </div>

                <div className="col-xl-12 mb-3">
                  <label className="form-label">
                    TYPE
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="type"
                        id="updateBasePrice"
                        value="Update Base Price"
                        checked={gst.type === "Update Base Price"}
                        onChange={(e) => setGst(prev => ({ ...prev, type: e.target.value }))}
                      />
                      <label className="form-check-label" htmlFor="updateBasePrice">
                        Update Base Price
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="type"
                        id="updateMRP"
                        value="Update MRP"
                        checked={gst.type === "Update MRP"}
                        onChange={(e) => setGst(prev => ({ ...prev, type: e.target.value }))}
                      />
                      <label className="form-check-label" htmlFor="updateMRP">
                        Update MRP
                      </label>
                    </div>
                  </div>
                  {error.type && <small className="text-danger d-block mt-2">{error.type}</small>}
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">Submitting...</span>
                  </>
                ) : id ? (
                  "UPDATE GST"
                ) : (
                  "ADD GST"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddGST;
